import { InvalidFilterError } from "./InvalidFilterError";
import {
  EQUALITY_OPERATORS,
  EqualityOperator,
  ORDER_OPERATORS,
  OrderOperator,
  SET_OPERATORS,
  SetOperator,
} from "./operators";
import type { FilterFieldSpec, FilterMatcher } from "./types";
import {
  CODE_PUSH_LABEL_KIND,
  EqualityValueKind,
  INTEGER_KIND,
  OrderedValueKind,
  STRING_KIND,
  VERSION_KIND,
} from "./valueKinds";

interface FieldSpecOptions<Item, Value, Operator> {
  // Reads the field from an item. `null`, `undefined` or an invalid value counts as missing.
  readonly getValue: (item: Item) => null | undefined | Value;
  readonly operators?: readonly Operator[]; // Narrows the operators this field accepts; defaults to the whole operator family.
}

export interface EqualityFieldSpecOptions<Item, Value> extends FieldSpecOptions<
  Item,
  Value,
  EqualityOperator
> {
  readonly equals?: (actual: Value, expected: Value) => boolean; // Replaces plain equality, e.g. a page section that also covers its sub-pages.
}

export type OrderedFieldSpecOptions<Item, Value> = FieldSpecOptions<Item, Value, OrderOperator>;
export type SetFieldSpecOptions<Item, Value> = FieldSpecOptions<
  Item,
  readonly Value[],
  SetOperator
>;

type ValueGuard<Value> = (value: unknown) => value is Value;

const readOperand = <Value>(isValue: ValueGuard<Value>, operand: unknown, path: string): Value => {
  if (!isValue(operand)) {
    throw new InvalidFilterError(path, `needs one valid value, got ${JSON.stringify(operand)}`);
  }
  return operand;
};

const readOperandList = <Value>(
  isValue: ValueGuard<Value>,
  operand: unknown,
  path: string
): readonly Value[] => {
  if (!Array.isArray(operand) || operand.length === 0 || !operand.every(isValue)) {
    throw new InvalidFilterError(
      path,
      `needs a non-empty list of valid values, got ${JSON.stringify(operand)}`
    );
  }
  return operand;
};

const negate =
  <Item>(test: FilterMatcher<Item>): FilterMatcher<Item> =>
  (item) =>
    !test(item);

export const createEqualityFieldSpec = <Item, Value>(
  kind: EqualityValueKind<Value>,
  options: EqualityFieldSpecOptions<Item, Value>
): FilterFieldSpec<Item> => {
  const equals = options.equals ?? kind.equals;
  const readActual = (item: Item): undefined | Value => {
    const actual = options.getValue(item);
    return kind.isValue(actual) ? actual : undefined;
  };
  return {
    compile: (operator, operand, path) => {
      if (operator === "$eq" || operator === "$ne") {
        const expected = readOperand(kind.isValue, operand, path);
        const isEqual: FilterMatcher<Item> = (item) => {
          const actual = readActual(item);
          return actual !== undefined && equals(actual, expected);
        };
        return operator === "$eq" ? isEqual : negate(isEqual);
      }
      if (operator === "$in" || operator === "$nin") {
        const expected = readOperandList(kind.isValue, operand, path);
        const isListed: FilterMatcher<Item> = (item) => {
          const actual = readActual(item);
          return actual !== undefined && expected.some((value) => equals(actual, value));
        };
        return operator === "$in" ? isListed : negate(isListed);
      }
      throw new InvalidFilterError(path, "is not an equality operator");
    },
    operators: options.operators ?? EQUALITY_OPERATORS,
  };
};

const ORDER_CHECKS: Readonly<Record<OrderOperator, (order: number) => boolean>> = {
  $eq: (order) => order === 0,
  $gt: (order) => order > 0,
  $gte: (order) => order >= 0,
  $lt: (order) => order < 0,
  $lte: (order) => order <= 0,
  $ne: (order) => order !== 0,
};

const ORDER_OPERATOR_KEYS: readonly string[] = ORDER_OPERATORS;
const isOrderOperator = (operator: string): operator is OrderOperator =>
  ORDER_OPERATOR_KEYS.includes(operator);

export const createOrderedFieldSpec = <Item, Value>(
  kind: OrderedValueKind<Value>,
  options: OrderedFieldSpecOptions<Item, Value>
): FilterFieldSpec<Item> => ({
  compile: (operator, operand, path) => {
    if (!isOrderOperator(operator)) {
      throw new InvalidFilterError(path, "is not an order operator");
    }
    const expected = readOperand(kind.isValue, operand, path);
    const check = ORDER_CHECKS[operator];
    return (item) => {
      const actual = options.getValue(item);
      // A missing value equals nothing, so only $ne holds.
      if (!kind.isValue(actual)) {
        return operator === "$ne";
      }
      return check(kind.compare(actual, expected));
    };
  },
  operators: options.operators ?? ORDER_OPERATORS,
});

export const createSetFieldSpec = <Item, Value>(
  kind: EqualityValueKind<Value>,
  options: SetFieldSpecOptions<Item, Value>
): FilterFieldSpec<Item> => ({
  compile: (operator, operand, path) => {
    const expected = readOperandList(kind.isValue, operand, path);
    const containsAll = operator === "$all";
    if (!containsAll && operator !== "$any") {
      throw new InvalidFilterError(path, "is not a set operator");
    }
    return (item) => {
      const actual = options.getValue(item);
      const members = Array.isArray(actual) ? actual.filter(kind.isValue) : [];
      const contains = (value: Value): boolean =>
        members.some((member) => kind.equals(member, value));
      return containsAll ? expected.every(contains) : expected.some(contains);
    };
  },
  operators: options.operators ?? SET_OPERATORS,
});

export const stringFieldSpec = <Item>(
  options: EqualityFieldSpecOptions<Item, string>
): FilterFieldSpec<Item> => createEqualityFieldSpec(STRING_KIND, options);

export const integerFieldSpec = <Item>(
  options: EqualityFieldSpecOptions<Item, number>
): FilterFieldSpec<Item> => createEqualityFieldSpec(INTEGER_KIND, options);

export const versionFieldSpec = <Item>(
  options: OrderedFieldSpecOptions<Item, string>
): FilterFieldSpec<Item> => createOrderedFieldSpec(VERSION_KIND, options);

export const codePushLabelFieldSpec = <Item>(
  options: OrderedFieldSpecOptions<Item, string>
): FilterFieldSpec<Item> => createOrderedFieldSpec(CODE_PUSH_LABEL_KIND, options);

export const stringSetFieldSpec = <Item>(
  options: SetFieldSpecOptions<Item, string>
): FilterFieldSpec<Item> => createSetFieldSpec(STRING_KIND, options);
